import { useCallback, useEffect, useMemo, useState } from 'react';
import yaml from 'js-yaml';
import { ChevronRight, Code, FormInput } from 'lucide-react';
import { AgentHierarchyView } from './AgentHierarchyView';
import type { RoleSummary } from './LeafConfigCard';
import {
  StreamFormView,
  YamlEditorView,
  yamlToFormData,
  formDataToYaml,
  type StreamFormData,
} from './StreamConfigEditDialog';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/Tabs';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { cn } from '../../lib/utils';
import { ZoomablePane } from '../ui/ZoomablePane';
import { DraftAiSideChat, type DraftAiSideChatMessage } from './DraftAiSideChat';

// ---------------------------------------------------------------------------
// Draft YAML → RoleSummary[] for org chart (matches flat + nested leaf shapes)
// ---------------------------------------------------------------------------

function draftYamlToRoleSummaries(streamKey: string, yamlContent: string): RoleSummary[] {
  let parsed: any;
  try {
    parsed = yaml.load(yamlContent);
  } catch {
    return [];
  }
  const root = parsed?.stream || parsed || {};
  const sid = String(root.id || streamKey);
  const streamDisplayName = String(root.displayName || root.id || streamKey);
  const rawStreamColor = root.color ?? root.streamColor ?? root.stream_color;
  const streamColor =
    typeof rawStreamColor === 'string' && rawStreamColor.trim() ? rawStreamColor.trim() : undefined;
  const workflowTypeCount = Array.isArray(root.taxonomy?.workflowTypes)
    ? root.taxonomy.workflowTypes.length
    : 0;
  const out: RoleSummary[] = [];

  const roles = Array.isArray(root.roles) ? root.roles : [];
  for (const role of roles) {
    const code = String(role.code || '').trim();
    const prods =
      Array.isArray(role.products) && role.products.length ? role.products.map(String) : [];
    const teams = Array.isArray(role.teams) && role.teams.length ? role.teams.map(String) : [];
    const tenants =
      Array.isArray(role.tenants) && role.tenants.length ? role.tenants.map(String) : [];
    if (!code || !prods.length || !teams.length || !tenants.length) continue;

    const parentRaw = String(role.parentRef || role.parent_ref || '').trim();
    const parentRef =
      parentRaw && !parentRaw.includes('/') ? `${sid}/${parentRaw}` : parentRaw || undefined;

    const enabled = role.enabled !== false;
    const adoProject = String(role.adoProject || role.ado_project || '').trim();
    const repo = String(role.repo || '').trim();
    const botDisplay = String(role.bot?.displayName || role.bot?.display_name || '').trim();
    const agentTitle = role.agentTitle || role.agent_title;
    const agentDescription = role.agentDescription || role.agent_description;

    out.push({
      streamId: sid,
      streamDisplayName,
      code,
      products: prods,
      teams,
      tenants,
      product: prods[0],
      productDisplayName: prods[0],
      teamType: teams[0],
      tenantCode: tenants[0],
      tenantDisplayName: tenants[0],
      agentTitle,
      agentDescription,
      parentRef,
      enabled,
      adoProject,
      repo,
      botDisplayName: botDisplay,
      workflowTypeCount,
      streamColor,
    });
  }

  const prodNested = Array.isArray(root.products) ? root.products : [];
  for (const p of prodNested) {
    const pid = String(p.id || '');
    if (!pid) continue;
    const teamTypes = Array.isArray(p.teamTypes) ? p.teamTypes : [];
    for (const tt of teamTypes) {
      const tid = String(tt.id || '');
      if (!tid) continue;
      const tns = Array.isArray(tt.tenants) ? tt.tenants : [];
      for (const t of tns) {
        const code = String(t.code || '');
        if (!code) continue;
        const dup = out.some(
          (r) => r.product === pid && r.teamType === tid && r.tenantCode === code,
        );
        if (dup) continue;
        const parentRaw = String(t.parentRef || t.parent_ref || '').trim();
        const parentRef =
          parentRaw && !parentRaw.includes('/') ? `${sid}/${parentRaw}` : parentRaw || undefined;
        out.push({
          streamId: sid,
          streamDisplayName,
          code,
          product: pid,
          productDisplayName: p.displayName || p.display_name || pid,
          teamType: tid,
          tenantCode: code,
          tenantDisplayName: t.displayName || t.display_name || code,
          agentTitle: t.agentTitle || t.agent_title,
          agentDescription: t.agentDescription || t.agent_description,
          parentRef,
          enabled: root.enabled !== false,
          adoProject: String(t.adoProject || t.ado_project || '').trim(),
          repo: String(t.repo || '').trim(),
          botDisplayName: String(t.bot?.displayName || t.bot?.display_name || '').trim(),
          workflowTypeCount,
          streamColor,
        });
      }
    }
  }

  return out;
}

// ---------------------------------------------------------------------------
// Agent hierarchy tree (flat definitions + roles), one stream
// ---------------------------------------------------------------------------

interface AgentHierarchyTreeNode {
  label: string;
  type: 'stream' | 'product' | 'team' | 'role';
  sublabel?: string;
  children: AgentHierarchyTreeNode[];
}

function buildAgentHierarchyFromYaml(yamlContent: string): AgentHierarchyTreeNode | null {
  try {
    const parsed = yaml.load(yamlContent) as any;
    const root = parsed?.stream || parsed;
    const streamNode: AgentHierarchyTreeNode = {
      label: root.displayName || root.id || '(unnamed)',
      type: 'stream',
      sublabel: root.id,
      children: [],
    };

    const products = root.productDefinitions || [];
    const teams = root.teamDefinitions || [];
    const roles = root.roles || [];

    for (const prod of products) {
      const prodNode: AgentHierarchyTreeNode = {
        label: prod.displayName || prod.id,
        type: 'product',
        sublabel: prod.id,
        children: [],
      };

      for (const team of teams) {
        const teamNode: AgentHierarchyTreeNode = {
          label: team.displayName || team.id,
          type: 'team',
          sublabel: team.id,
          children: [],
        };

        const matchingRoles = roles.filter(
          (r: any) =>
            (r.products || []).includes(prod.id) && (r.teams || []).includes(team.id),
        );
        for (const role of matchingRoles) {
          teamNode.children.push({
            label: role.agentTitle || role.code,
            type: 'role',
            sublabel: role.code,
            children: [],
          });
        }

        if (teamNode.children.length > 0) {
          prodNode.children.push(teamNode);
        }
      }

      const unteamedRoles = roles.filter(
        (r: any) =>
          (r.products || []).includes(prod.id) && (!r.teams || r.teams.length === 0),
      );
      for (const role of unteamedRoles) {
        prodNode.children.push({
          label: role.agentTitle || role.code,
          type: 'role',
          sublabel: role.code,
          children: [],
        });
      }

      streamNode.children.push(prodNode);
    }

    const unproductedRoles = roles.filter(
      (r: any) => !r.products || r.products.length === 0,
    );
    for (const role of unproductedRoles) {
      streamNode.children.push({
        label: role.agentTitle || role.code,
        type: 'role',
        sublabel: role.code,
        children: [],
      });
    }

    return streamNode;
  } catch {
    return null;
  }
}

const TREE_NODE_COLORS: Record<string, string> = {
  stream: 'border-violet-500 bg-violet-50 dark:bg-violet-900/20',
  product: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
  team: 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20',
  role: 'border-orange-500 bg-orange-50 dark:bg-orange-900/20',
};

const TREE_NODE_BADGES: Record<string, string> = {
  stream: 'Stream',
  product: 'Product',
  team: 'Team',
  role: 'Role',
};

function DraftHierarchyTreeNode({
  node,
  depth = 0,
}: {
  node: AgentHierarchyTreeNode;
  depth?: number;
}) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;

  return (
    <div className={cn('flex flex-col', depth > 0 && 'ml-6')}>
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg border-l-4 mb-1.5',
          TREE_NODE_COLORS[node.type],
        )}
      >
        {hasChildren ? (
          <button type="button" onClick={() => setExpanded(!expanded)} className="p-0.5">
            <ChevronRight
              className={cn('w-3.5 h-3.5 transition-transform', expanded && 'rotate-90')}
            />
          </button>
        ) : (
          <span className="w-4.5" />
        )}
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{node.label}</span>
          {node.sublabel && node.sublabel !== node.label && (
            <span className="text-xs text-gray-400 dark:text-gray-500 ml-1.5">({node.sublabel})</span>
          )}
        </div>
        <Badge variant="outline" className="text-[9px] shrink-0">
          {TREE_NODE_BADGES[node.type]}
        </Badge>
      </div>
      {expanded && hasChildren && (
        <div className="flex flex-col">
          {node.children.map((child, i) => (
            <DraftHierarchyTreeNode
              key={`${child.type}-${child.sublabel}-${i}`}
              node={child}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

export interface DraftHierarchyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draftYamls: Record<string, string>;
  /** Stream tab id to select when opening (e.g. active YAML tab). */
  initialStreamId?: string;
  onDraftYamlChange: (streamId: string, yaml: string) => void;
  chat: {
    messages: DraftAiSideChatMessage[];
    input: string;
    sending: boolean;
    disabled?: boolean;
    statusLine?: string;
    onInputChange: (value: string) => void;
    onSend: () => void;
  };
}

export function DraftHierarchyDialog({
  open,
  onOpenChange,
  draftYamls,
  initialStreamId,
  onDraftYamlChange,
  chat,
}: DraftHierarchyDialogProps) {
  const streamIds = Object.keys(draftYamls);
  const streamsKey = [...streamIds].sort().join(',');
  const [selectedStreamId, setSelectedStreamId] = useState('');
  const [mainTab, setMainTab] = useState('agent-tree');
  const [yamlContent, setYamlContent] = useState('');
  const [formData, setFormData] = useState<StreamFormData | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || streamIds.length === 0) return;
    const pick =
      initialStreamId && draftYamls[initialStreamId] !== undefined
        ? initialStreamId
        : streamIds[0];
    setSelectedStreamId(pick);
  }, [open, initialStreamId, streamsKey]);

  useEffect(() => {
    if (!open) return;
    setMainTab('agent-tree');
  }, [open]);

  useEffect(() => {
    if (!open || !selectedStreamId) return;
    setMainTab('agent-tree');
  }, [open, selectedStreamId]);

  const loadStreamYaml = useCallback(
    (id: string) => {
      const y = draftYamls[id] ?? '';
      setYamlContent(y);
      try {
        setFormData(yamlToFormData(y));
        setParseError(null);
      } catch (err: any) {
        setFormData(null);
        setParseError(err.message);
      }
    },
    [draftYamls],
  );

  useEffect(() => {
    if (open && selectedStreamId && draftYamls[selectedStreamId] !== undefined) {
      loadStreamYaml(selectedStreamId);
    }
  }, [open, selectedStreamId, draftYamls, loadStreamYaml]);

  const flushFormToYaml = useCallback(() => {
    if (!formData || !selectedStreamId) return;
    try {
      const next = formDataToYaml(formData, yamlContent);
      yaml.load(next);
      setYamlContent(next);
      setParseError(null);
      onDraftYamlChange(selectedStreamId, next);
    } catch (err: any) {
      setParseError(err.message);
    }
  }, [formData, yamlContent, selectedStreamId, onDraftYamlChange]);

  const handleMainTabChange = (v: string) => {
    if (mainTab === 'stream-form' && (v === 'stream-yaml' || v === 'org-chart' || v === 'agent-tree')) {
      flushFormToYaml();
    }
    if (mainTab === 'stream-yaml' && v === 'stream-form') {
      try {
        setFormData(yamlToFormData(yamlContent));
        setParseError(null);
      } catch (err: any) {
        setParseError(err.message);
      }
    }
    setMainTab(v);
  };

  const handleYamlChange = (v: string) => {
    setYamlContent(v);
    try {
      yaml.load(v);
      setParseError(null);
    } catch (err: any) {
      setParseError(err.message);
    }
    if (selectedStreamId) onDraftYamlChange(selectedStreamId, v);
  };

  /** All streams in the draft session — needed so parentRef across streams (e.g. management/…) resolves. */
  const allDraftRoles = useMemo(
    () => streamIds.flatMap((id) => draftYamlToRoleSummaries(id, draftYamls[id] ?? '')),
    [streamsKey, draftYamls],
  );

  const agentTreeRoot = useMemo(() => buildAgentHierarchyFromYaml(yamlContent), [yamlContent]);

  const noop = useCallback(() => {}, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent maxWidth="full" className="max-h-[92vh] h-[92vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Draft stream: hierarchy & editor</DialogTitle>
        </DialogHeader>
        <DialogBody className="flex-1 min-h-0 flex overflow-hidden p-0">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-hidden px-6 py-4">
          {streamIds.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
              No draft streams in this session.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-1 border-b border-gray-200 dark:border-gray-700 pb-2">
                {streamIds.map((id) => (
                  <Button
                    key={id}
                    type="button"
                    size="sm"
                    variant={selectedStreamId === id ? 'default' : 'outline'}
                    onClick={() => {
                      if (mainTab === 'stream-form') flushFormToYaml();
                      setSelectedStreamId(id);
                    }}
                  >
                    {id}
                  </Button>
                ))}
              </div>

              <Tabs value={mainTab} onValueChange={handleMainTabChange} className="flex-1 flex flex-col min-h-0">
                <TabsList className="flex flex-wrap h-auto gap-1 py-1 shrink-0">
                  <TabsTrigger value="agent-tree">Agent hierarchy</TabsTrigger>
                  <TabsTrigger value="org-chart">Org chart</TabsTrigger>
                  <TabsTrigger value="stream-form">Stream form</TabsTrigger>
                  <TabsTrigger value="stream-yaml">YAML</TabsTrigger>
                </TabsList>

                <TabsContent value="agent-tree" className="flex-1 min-h-0 overflow-hidden mt-3 flex flex-col">
                  {!agentTreeRoot || agentTreeRoot.children.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                      No agent hierarchy tree (needs productDefinitions / teamDefinitions / roles).
                    </p>
                  ) : (
                    <ZoomablePane>
                      <DraftHierarchyTreeNode node={agentTreeRoot} />
                    </ZoomablePane>
                  )}
                </TabsContent>

                <TabsContent value="org-chart" className="flex-1 min-h-0 overflow-hidden mt-3 flex flex-col">
                  {allDraftRoles.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                      No roles or nested products to show in this draft session.
                    </p>
                  ) : (
                    <AgentHierarchyView
                      className="min-h-0 flex-1"
                      roles={allDraftRoles}
                      allRoles={allDraftRoles}
                      canManage={false}
                      onEditRole={noop}
                      onPauseToggle={noop}
                      onDelete={noop}
                    />
                  )}
                </TabsContent>

                <TabsContent value="stream-form" className="flex-1 min-h-0 overflow-y-auto mt-3">
                  <div className="flex items-center justify-end gap-2 mb-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400 mr-auto">
                      Edits apply to this draft session; use Apply from reconciliation to persist.
                    </span>
                  </div>
                  {formData ? (
                    <StreamFormView
                      data={formData}
                      onChange={(next) => {
                        setFormData(next);
                      }}
                    />
                  ) : (
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {parseError || 'Could not parse YAML into form.'}
                    </p>
                  )}
                </TabsContent>

                <TabsContent value="stream-yaml" className="flex-1 min-h-0 flex flex-col mt-3">
                  <div className="flex items-center gap-2 mb-2 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden w-fit">
                    <button
                      type="button"
                      onClick={() => handleMainTabChange('stream-form')}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                    >
                      <FormInput className="w-3.5 h-3.5" /> Form
                    </button>
                    <button
                      type="button"
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400"
                    >
                      <Code className="w-3.5 h-3.5" /> YAML
                    </button>
                  </div>
                  <YamlEditorView content={yamlContent} onChange={handleYamlChange} parseError={parseError} />
                </TabsContent>
              </Tabs>
            </>
          )}
          </div>
          <DraftAiSideChat
            messages={chat.messages}
            input={chat.input}
            sending={chat.sending}
            disabled={chat.disabled}
            statusLine={chat.statusLine}
            onInputChange={chat.onInputChange}
            onSend={chat.onSend}
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
